import FnEditorAutoSave from "./FnEditorAutoSave";
import FnEditorLogger from "./FnEditorDebugger";
import FnEditorDom from "./FnEditorDom";
import FnEditorElementMap from "./FnEditorElementMap";
import FnEditorId from "./FnEditorId";
import FnEditorSelection from "./FnEditorSelection";
import FnEditorStack from "./FnEditorStack";
import FnEditorState from "./FnEditorState";
import { FnEditorElementType } from "./types";
import { v4 as uuidv4 } from 'uuid';
const debugMode = false;
export default class FnEditor {
    constructor(editorRoot, identifier, lines) {
        this.editorRoot = editorRoot;
        this.identifier = identifier;
        this.tempActions = [];
        this.onSaveStateChangeHandler = [];
        const linesWithId = lines.map((line) => {
            var _a;
            return Object.assign(Object.assign({}, line), { id: (_a = line.id) !== null && _a !== void 0 ? _a : uuidv4() });
        });
        this.logger = new FnEditorLogger(debugMode);
        this.stack = new FnEditorStack(this.logger);
        const ids = new FnEditorId();
        const elements = new FnEditorElementMap(ids, this.logger, linesWithId);
        this.state = new FnEditorState(elements, new FnEditorSelection(elements.getFirst().getId(), 0));
        this.dom = new FnEditorDom(this.editorRoot, this.identifier, ids, this.logger);
        this.syncStateWithDom();
        this.autoSave = new FnEditorAutoSave(this.state, this.logger);
        const listener = {
            actionSet: (actions) => {
                if (this.tempActions.length > 0) {
                    this.stack.add([...this.tempActions]);
                    this.tempActions = [];
                }
                this.executeActions(actions, 'apply');
                this.stack.add(actions);
            },
            mergeableAction: (action) => {
                this.executeActions([action], 'apply');
                if (this.tempActions.length > 0) {
                    const previousAction = this.tempActions[this.tempActions.length - 1];
                    const wasMerged = action.mergeWithPrevious(previousAction.type, previousAction.getDataToMergeWithNext(action.type));
                    if (wasMerged) {
                        this.tempActions[this.tempActions.length - 1] = action;
                    }
                    else {
                        this.stack.add([...this.tempActions]);
                        this.tempActions = [action];
                    }
                }
                else {
                    this.tempActions.push(action);
                }
            },
            undo: () => {
                this.undo();
            },
            redo: () => {
                this.redo();
            },
            showHistory: () => {
                const entries = this.autoSave.getEntries();
                const ul = document.createElement('ul');
                ul.innerHTML = entries.map((entry) => {
                    return `<li><strong>${entry.title}</strong><button data-timestamp="${entry.timestamp}">Restore</button></li>`;
                }).join("\n");
                ul.classList.add('fn-history');
                ul.querySelectorAll('button').forEach((button) => {
                    button.addEventListener('click', (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        const toRestore = this.autoSave.getEntryForRestore(parseInt(button.getAttribute('data-timestamp') || '0'));
                        if (toRestore) {
                            this.stack.clear();
                            this.state = new FnEditorState(new FnEditorElementMap(ids, this.logger, toRestore.elements), FnEditorSelection.fromRaw(toRestore.selection));
                            this.syncStateWithDom();
                        }
                    });
                });
                document.body.appendChild(ul);
                const closeButton = document.createElement('button');
                closeButton.classList.add('fn-history-close');
                closeButton.title = 'Close';
                closeButton.innerHTML = '&times;';
                closeButton.addEventListener('click', () => {
                    closeButton.remove();
                    ul.remove();
                });
                ul.insertAdjacentElement('beforebegin', closeButton);
            },
            showNavigator: () => {
                const navigator = document.createElement('div');
                navigator.classList.add('fn-navigator');
                let lastSlugline;
                for (const element of this.state.elements.getAll()) {
                    if (element.getType() === FnEditorElementType.Blank) {
                        continue;
                    }
                    if (element.getType() === FnEditorElementType.SceneHeading) {
                        if (lastSlugline) {
                            navigator.appendChild(lastSlugline);
                        }
                        lastSlugline = document.createElement('div');
                        lastSlugline.insertAdjacentHTML("beforeend", `<h2>${element.getText()}</h2>`);
                        lastSlugline.addEventListener('click', () => {
                            this.dom.scrollTo(element.getId());
                        });
                        continue;
                    }
                    if (lastSlugline) {
                        if (lastSlugline.textContent.length < 300) {
                            lastSlugline.insertAdjacentHTML("beforeend", `<span>${element.getText()}</span>`);
                        }
                    }
                }
                if (lastSlugline) {
                    navigator.appendChild(lastSlugline);
                }
                const closeButton = document.createElement('button');
                closeButton.classList.add('fn-navigator-close');
                closeButton.title = 'Close';
                closeButton.innerHTML = '&times;';
                closeButton.addEventListener('click', () => {
                    closeButton.remove();
                    navigator.remove();
                });
                document.body.appendChild(navigator);
                navigator.insertAdjacentElement('afterend', closeButton);
            }
        };
        this.dom.addListener(listener);
    }
    /**public methods to connect with frontend components */
    undo() {
        if (this.tempActions.length > 0) {
            this.stack.add([...this.tempActions]);
            this.tempActions = [];
        }
        const actions = this.stack.undo();
        if (actions) {
            this.executeActions(actions, 'undo');
        }
    }
    redo() {
        if (this.tempActions.length > 0) {
            this.stack.add([...this.tempActions]);
            this.tempActions = [];
        }
        const actions = this.stack.redo();
        if (actions) {
            this.executeActions(actions, 'apply');
        }
    }
    destroy() {
        this.dom.destroy();
        this.autoSave.destroy();
    }
    save() {
        this.onSaveStateChangeHandler.forEach(handler => {
            handler(true);
        });
        return this.state.elements.toRawLines();
    }
    onSaveStateChange(handler) {
        this.onSaveStateChangeHandler.push(handler);
    }
    /** end */
    executeActions(actions, type) {
        this.onSaveStateChangeHandler.forEach(handler => {
            handler(false);
        });
        const evaluateIds = [];
        if (type === 'undo') {
            actions = [...actions].reverse();
            // We get the last selection change and do that last.
            const lastSelectionChange = actions.filter(action => action.type === 'changeSelection').pop();
            actions = actions.filter(action => action.type !== 'changeSelection');
            if (lastSelectionChange) {
                actions.push(lastSelectionChange);
            }
        }
        actions.forEach((action) => {
            let actionResult;
            if (type === 'undo') {
                this.logger.log('Undoing action', action);
                actionResult = action.undo(this.state, this.dom, this.logger);
            }
            else {
                this.logger.log('Applying action', action);
                actionResult = action.apply(this.state, this.dom, this.logger);
            }
            evaluateIds.push(...actionResult.idsToEvaluate);
        });
        const evaluateResult = this.state.elements.evaluateIds(evaluateIds);
        evaluateResult.elementsWithChangedType.forEach((element) => {
            element.syncType(this.dom.getById(element.getId()));
        });
    }
    syncStateWithDom() {
        this.dom.clear();
        for (const fnEditorElement of this.state.elements.getAll()) {
            const domElement = this.dom.insertLast(fnEditorElement.getId());
            fnEditorElement.syncWithDom(domElement);
            fnEditorElement.syncType(domElement);
        }
        this.dom.setSelection(this.state.selection);
    }
}
